/**
 * Axios Interceptors
 * Request and Response interceptors for centralized handling
 */

import API_CONFIG from '../config/api.config';
import { describeApiError } from '../helpers/apiError';

/**
 * Request Interceptor
 * Automatically injects authentication token into requests
 */
export const requestInterceptor = (config) => {
  // Get token from localStorage
  const token = localStorage.getItem(API_CONFIG.TOKEN.STORAGE_KEY);

  // Add authorization header if token exists
  if (token) {
    config.headers[API_CONFIG.TOKEN.HEADER_NAME] = `${API_CONFIG.TOKEN.PREFIX} ${token}`;
  }

  // Log request in development mode
  if (API_CONFIG.IS_DEVELOPMENT) {
    console.log('📤 Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: config.data,
      headers: config.headers,
    });
  }

  return config;
};

/**
 * Request Error Interceptor
 */
export const requestErrorInterceptor = (error) => {
  if (API_CONFIG.IS_DEVELOPMENT) {
    console.error('❌ Request Error:', error);
  }
  return Promise.reject(error);
};

/**
 * Response Interceptor
 * Handles successful responses
 */
export const responseInterceptor = (response) => {
  // Log response in development mode
  if (API_CONFIG.IS_DEVELOPMENT) {
    console.log('📥 Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });
  }

  return response;
};

/**
 * Response Error Interceptor
 * Centralized error handling for all API responses.
 *
 * Rejects with `{ status, code, action, retryable, requestId, message, data,
 * response, originalError }`, where `message` is always calm, translated text
 * that is safe to show (see helpers/apiError.js). `response` is kept so code
 * that reads `err.response.data` keeps working.
 */

// Requests where a 401 means "wrong details", not "your session ended":
// reloading the page there would wipe the error before the user sees it.
const AUTH_FORM_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/change-password',
];

export const responseErrorInterceptor = (error) => {
  const status = error.response?.status;
  const data = error.response?.data;
  const url = error.config?.url || '';
  const info = describeApiError(error);

  if (API_CONFIG.IS_DEVELOPMENT) {
    console.error('❌ Response Error:', {
      status,
      code: info.code,
      message: info.message,
      technical: data?.technicalMessage || error.message,
      url,
      error: data,
    });
  }

  if (status === 401 && !AUTH_FORM_PATHS.some((path) => url.includes(path))) {
    handleUnauthorized();
  }

  return Promise.reject({
    isApiError: true,
    status,
    code: info.code,
    action: info.action,
    retryable: info.retryable,
    requestId: info.requestId,
    message: info.message,
    data,
    response: error.response,
    originalError: error,
  });
};

/**
 * Handle unauthorized access
 * Clear authentication and redirect to login
 */
const handleUnauthorized = () => {
  // Clear authentication token
  localStorage.removeItem(API_CONFIG.TOKEN.STORAGE_KEY);

  // Get current language for redirect
  const currentPath = window.location.pathname;
  const language = currentPath.startsWith('/ar') ? 'ar' : 'en';

  // Redirect to login page
  window.location.href = `/${language}/login`;

  console.warn('Session expired. Please login again.');
};
